// poi-map.jsx — nearby points of interest (parks, dog parks, peaks, viewpoints)
// via /api/ext/poi-nearby (Overpass), rendered as a Leaflet map + tabbed list.
// Brand-aware: dog parks lead when icon=paw (WeatherDog).

import { useEffect, useRef, useState } from 'preact/hooks';
import { loadLeaflet, whenInView } from './lazy-leaflet.js';
import { getRawState, subscribe } from '../data-bridge.js';

function proxyTileUrl(rawTpl) {
  return '/api/ext/img?url=' + encodeURIComponent(rawTpl)
    .replace(/%7B/g, '{').replace(/%7D/g, '}');
}
const cartoBasemapTpl = () => 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png';

const POI_TABS_BASE = [
  { id: 'park',      label: 'Parks',     pinColor: '#22c55e' },
  { id: 'dog_park',  label: 'Dog parks', pinColor: '#facc15' },
  { id: 'peak',      label: 'Peaks',     pinColor: '#a78bfa' },
  { id: 'viewpoint', label: 'Views',     pinColor: '#60a5fa' },
];

// Shown when the lookup fails. The map above still shows the place.
const POI_UNAVAILABLE = "Nearby places aren't available right now.";

async function fetchPoiNearby(lat, lon) {
  try {
    const r = await fetch(`/api/ext/poi-nearby?lat=${lat.toFixed(4)}&lon=${lon.toFixed(4)}`, { cache: 'force-cache' });
    if (!r.ok) return null;
    const j = await r.json();
    return j && j.ok ? j : null;
  } catch (_) { return null; }
}

export function PoiNearby({ brand, height = 280, interactive: alwaysInteractive = false }) {
  const wrapRef = useRef(null);
  const mapDivRef = useRef(null);
  const stateRef = useRef({ map: null, base: null, markerGroup: null, youArehere: null });
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState(brand.icon === 'paw' ? 'dog_park' : 'park');
  const [loadedMap, setLoadedMap] = useState(false);
  const [error, setError] = useState(null);
  const [interactive, setInteractive] = useState(alwaysInteractive);

  // Tabs ordered with dog_park first on weatherdog
  const tabs = brand.icon === 'paw'
    ? [POI_TABS_BASE[1], POI_TABS_BASE[0], POI_TABS_BASE[2], POI_TABS_BASE[3]]
    : POI_TABS_BASE;

  // Fetch POIs after first scroll-into-view (don't waste data if section never seen)
  useEffect(() => {
    if (!wrapRef.current) return;
    const stop = whenInView(wrapRef.current, async () => {
      const loc = getRawState().location;
      if (!loc) { setError('No location'); return; }
      try {
        const L = await loadLeaflet();
        if (!mapDivRef.current) return;
        const map = L.map(mapDivRef.current, {
          center: [loc.lat, loc.lon], zoom: 13,
          zoomControl: interactive, attributionControl: true, scrollWheelZoom: false,
          dragging: interactive, touchZoom: interactive, doubleClickZoom: interactive,
          boxZoom: false, keyboard: false,
        });
        const base = L.tileLayer(proxyTileUrl(cartoBasemapTpl()), {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/">CARTO</a>',
          subdomains: 'abcd', maxZoom: 19,
        }).addTo(map);
        stateRef.current.map = map;
        stateRef.current.base = base;
        setLoadedMap(true);
        setTimeout(() => map.invalidateSize(), 100);

        const j = await fetchPoiNearby(loc.lat, loc.lon);
        if (!j) { setError(POI_UNAVAILABLE); return; }
        setData(j);
        renderMarkers(L, j.results, loc);
      } catch (e) {
        console.warn('[m] poi init failed:', e);
        setError('Map failed to load');
      }
    });
    return () => {
      stop();
      const st = stateRef.current;
      if (st.map) { try { st.map.remove(); } catch (_) {} }
    };
  }, []);

  // React to location changes
  useEffect(() => {
    return subscribe(async (s) => {
      const st = stateRef.current;
      if (!st.map || !s.location) return;
      st.map.setView([s.location.lat, s.location.lon], 13);
      const j = await fetchPoiNearby(s.location.lat, s.location.lon);
      if (j) {
        setError(null);
        setData(j);
        const L = window.L;
        if (L) renderMarkers(L, j.results, s.location);
      } else {
        // Don't leave the previous place's list and pins under the new place.
        setData(null);
        setError(POI_UNAVAILABLE);
        clearMarkers();
      }
    });
  }, []);

  function clearMarkers() {
    const st = stateRef.current;
    if (!st.map) return;
    if (st.markerGroup) { st.map.removeLayer(st.markerGroup); st.markerGroup = null; }
    if (st.youArehere) { st.map.removeLayer(st.youArehere); st.youArehere = null; }
  }

  function renderMarkers(L, results, center) {
    const st = stateRef.current;
    if (!st.map) return;
    clearMarkers();
    st.markerGroup = L.layerGroup();
    let added = 0;
    for (const tab of POI_TABS_BASE) {
      const items = results[tab.id] || [];
      for (const item of items) {
        // Classes, not style="" — the tenant CSP (style-src 'self') blocks
        // inline style attributes in parsed HTML strings like divIcon html /
        // popup content. Pin colors live in mobile.css (.m-poi-pin--<id>).
        const m = L.marker([item.lat, item.lon], {
          icon: L.divIcon({
            html: `<span class="m-poi-pin m-poi-pin--${tab.id}"></span>`,
            className: '', iconSize: [18, 18], iconAnchor: [9, 9],
          }),
        });
        const distKm = item.distance_m < 950 ? `${item.distance_m} m` : `${(item.distance_m / 1000).toFixed(1)} km`;
        const safeName = String(item.name || '(unnamed)').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        const link = item.osm_url ? `<br><a href="${item.osm_url}" target="_blank" rel="noopener">view on OSM ↗</a>` : '';
        m.bindPopup(`<strong>${safeName}</strong><br><span class="m-poi-popup__dist">${distKm} away</span>${link}`);
        st.markerGroup.addLayer(m);
        added++;
      }
    }
    st.markerGroup.addTo(st.map);
    st.youArehere = L.circleMarker([center.lat, center.lon], {
      radius: 7, color: brand.accent, fillColor: brand.accent, fillOpacity: 1, weight: 3,
    }).bindPopup('You are here').addTo(st.map);
  }

  const items = data?.results?.[activeTab] || [];

  const enableInteractive = () => {
    if (interactive) return;
    setInteractive(true);
    const st = stateRef.current;
    if (st.map) {
      try {
        st.map.dragging.enable();
        st.map.touchZoom.enable();
        st.map.doubleClickZoom.enable();
      } catch (_) {}
    }
  };

  return (
    <div ref={wrapRef}>
      <div className={`m-map-wrap${interactive ? ' is-interactive' : ''}`} style={{ position: 'relative', marginBottom: 12 }}>
        <div ref={mapDivRef} style={{
          height, width: '100%', borderRadius: 16, overflow: 'hidden',
          border: `1px solid ${brand.line}`, background: '#0a1220',
        }}/>
        {!interactive && loadedMap && (
          <div className="m-map-overlay" onClick={enableInteractive}>
            <span className="m-map-overlay-pill">Tap to interact</span>
          </div>
        )}
      </div>
      {/* Tabs */}
      <div style={{ display: 'flex', gap: 6, padding: '0 0 8px', overflowX: 'auto' }} className="wx-hide-scroll">
        {tabs.map((t) => {
          const active = t.id === activeTab;
          const count = data?.results?.[t.id]?.length ?? 0;
          return (
            <button key={t.id} onClick={() => setActiveTab(t.id)} className="wx-tap" style={{
              flexShrink: 0, padding: '6px 12px', borderRadius: 999,
              background: active ? `${t.pinColor}22` : brand.panel,
              color: active ? brand.text : brand.textDim,
              border: `1px solid ${active ? t.pinColor : brand.line}`,
              fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
            }}>
              <span style={{ width: 8, height: 8, borderRadius: 4, background: t.pinColor }} aria-hidden="true"/>
              {t.label} {count > 0 && <span style={{ opacity: .65 }}>· {count}</span>}
            </button>
          );
        })}
      </div>
      {/* Item list */}
      <div>
        {error && <div style={{ fontSize: 12, color: brand.textDim, padding: 8 }}>{error}</div>}
        {!error && !data && !loadedMap && <div style={{ fontSize: 12, color: brand.textDim, padding: 8 }}>Scroll into view to load map…</div>}
        {!error && data && items.length === 0 && (
          <div style={{ fontSize: 12, color: brand.textDim, padding: 8 }}>None found in this area.</div>
        )}
        {items.slice(0, 8).map((item, i) => {
          const distKm = item.distance_m < 950 ? `${item.distance_m} m` : `${(item.distance_m / 1000).toFixed(1)} km`;
          return (
            <a key={i} href={item.osm_url || '#'} target="_blank" rel="noopener" style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '10px 4px',
              borderBottom: `1px solid ${brand.line}`, textDecoration: 'none', color: brand.text,
            }}>
              <span style={{ width: 14, height: 14, borderRadius: 7, background: POI_TABS_BASE.find((t) => t.id === activeTab)?.pinColor, flexShrink: 0 }}/>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.name || '(unnamed)'}</div>
                <div style={{ fontSize: 11, color: brand.textDim, marginTop: 1 }}>
                  {distKm} away{item.dog_allowed === true ? ' · dog-friendly' : ''}{item.dog_allowed === false ? ' · no dogs' : ''}
                </div>
              </div>
            </a>
          );
        })}
      </div>
    </div>
  );
}
